import { RemoteDataSource, Settings } from '@/datasources/remote_datasource';
import { Failure } from '@/usecases/repository';
import { AppConfig } from '@/datasources/AppConfig';
import { FotoPerfil } from '@/types/media';
import { encryptId, decryptId } from '@/utils/tokenCrypto';

export interface OuvidoriaCompacta {
  id: string;
  municipioId?: string;
  municipio?: {
    id: number;
    guid?: string;
    codigoIBGE?: string;
    nome?: string;
  };
  nome: string;
  cnpj?: string;
  fotoPerfil?: FotoPerfil;
  geral?: boolean;
  arquivada?: boolean;
  logo?: {
    id: string;
    arquivo: string;
  };
  razaoSocial?: string;
  naturezaJuridica?: string;
  nomeFantasia?: string;
  email?: string;
  telefone?: string;
  integracao?: string;
  etiquetas?: string[];
  latitude?: number;
  longitude?: number;
  altitude?: number;
  endereco?: {
    logradouro?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
    cep?: string;
    pais?: string;
    ibge?: string;
  };
  agentes?: string[];
}

export interface Agente {
  id: string;
  nome: string;
  email?: string;
  telefone?: string;
  fotoPerfil?: FotoPerfil;
  ouvidoria: OuvidoriaCompacta;
  status: string;
  gestor: boolean;
  intergracao?: string;
  arquivado: boolean;
}

class AgenteService {
  private readonly remoteDataSource: RemoteDataSource;

  constructor() {
    this.remoteDataSource = new RemoteDataSource(Settings.urlBase);
  }

  /**
   * Obtém o agente atual com base no usuário autenticado (lê o id do JWT).
   * GET /urbnet/agente
   */
  async obterAgenteAtual(): Promise<{ success: Agente } | { failure: Failure }> {
    // Limpa dados de agente anterior antes de buscar o agente atual,
    // evitando que IDs obsoletos (de outro agente/máquina) contaminem o estado.
    this.limparCache();

    const response = await this.remoteDataSource.get<Agente>({
      endpoint: 'agente',
      requiredToken: true,
      headers: { Accept: 'application/vnd.data.full.v1+json' },
      forceRefresh: true,
    });

    if ('failure' in response) {
      return response;
    }

    // O endpoint /urbnet/agente retorna um único objeto Agente (não array)
    const rawBody = response.success.body ?? response.success.data;
    let body: Agente | null = null;

    if (rawBody && typeof rawBody === 'object') {
      body = (Array.isArray(rawBody) ? rawBody[0] : rawBody) as Agente;
    }

    if (!body || !body.id) {
      return { failure: new Failure({ messages: ['Resposta inválida ao obter agente'] }) };
    }

    if (!body.ouvidoria?.id || body.ouvidoria?.municipioId == null) {
      return {
        failure: new Failure({
          messages: ['Agente retornado sem ouvidoria/município associados'],
        }),
      };
    }

    // Persistir IDs em memória + localStorage estritamente a partir da resposta.
    AppConfig.agenteId = body.id;
    AppConfig.ouvidoriaId = body.ouvidoria.id;
    AppConfig.municipioId = String(body.ouvidoria.municipioId);
    // `municipio.id` é o identificador numérico (int64) do município, exigido por
    // endpoints como o de checkout de pagamento. Difere do `municipioId` (GUID).
    if (body.ouvidoria.municipio?.id != null) {
      AppConfig.municipioLong = Number(body.ouvidoria.municipio.id);
    }

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('agente', JSON.stringify(body));
      // Encriptar IDs antes de salvar no localStorage
      const [agenteIdCifrado, ouvidoriaIdCifrado, municipioIdCifrado] = await Promise.all([
        encryptId(AppConfig.agenteId),
        encryptId(AppConfig.ouvidoriaId),
        encryptId(AppConfig.municipioId),
      ]);
      localStorage.setItem('agenteId', agenteIdCifrado);
      localStorage.setItem('ouvidoriaId', ouvidoriaIdCifrado);
      localStorage.setItem('municipioId', municipioIdCifrado);

      if (AppConfig.municipioLong != null && !Number.isNaN(AppConfig.municipioLong)) {
        const municipioLongCifrado = await encryptId(String(AppConfig.municipioLong));
        localStorage.setItem('municipioLong', municipioLongCifrado);
      }
    }

    return { success: body };
  }

  /**
   * Recupera o agente armazenado em localStorage (sem nova requisição).
   */
  getAgenteCache(): Agente | null {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem('agente');
    if (!raw) return null;
    try {
      return JSON.parse(raw) as Agente;
    } catch {
      return null;
    }
  }

  /**
   * Limpa cache do agente (usar no logout).
   */
  limparCache(): void {
    AppConfig.agenteId = undefined as unknown as string;
    AppConfig.ouvidoriaId = undefined as unknown as string;
    AppConfig.municipioId = undefined as unknown as string;
    AppConfig.municipioLong = undefined as unknown as number;
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('agente');
      localStorage.removeItem('agenteId');
      localStorage.removeItem('ouvidoriaId');
      localStorage.removeItem('municipioId');
      localStorage.removeItem('municipioLong');
      // Limpa aceite de termos para que apareça novamente no próximo login
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('urbnet:termosAceitos:')) {
          localStorage.removeItem(key);
        }
      });
    }
  }

  /**
   * Atualiza a foto de perfil do agente.
   * POST /urbnet/agente/{agenteId}/fotoPerfil
   */
  async atualizarFotoPerfil(
    agenteId: string,
    arquivo: File
  ): Promise<{ success: FotoPerfil } | { failure: Failure }> {
    const formData = new FormData();
    formData.append('fotoPerfil', arquivo);

    const response = await this.remoteDataSource.post<FotoPerfil>({
      endpoint: `agente/${agenteId}/fotoPerfil`,
      body: formData,
      headers: {
        'Accept': 'application/vnd.data.minimal.v1+json',
      },
      requiredToken: true,
    });

    if ('failure' in response) {
      return response;
    }

    const body = response.success.body as FotoPerfil;
    if (!body || !body.id) {
      return { failure: new Failure({ messages: ['Resposta inválida ao atualizar foto de perfil'] }) };
    }

    // Atualizar cache do agente com nova foto
    const agenteCache = this.getAgenteCache();
    if (agenteCache) {
      agenteCache.fotoPerfil = body;
      localStorage.setItem('agente', JSON.stringify(agenteCache));
    }

    return { success: body };
  }

  /**
   * Baixa uma imagem de uma URL e converte para File.
   */
  async baixarImagemComoFile(url: string, filename: string = 'profile.jpg'): Promise<File | null> {
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      const blob = await response.blob();
      return new File([blob], filename, { type: blob.type });
    } catch {
      return null;
    }
  }
}

export const agenteService = new AgenteService();
